import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SaveMethod,
  chooseDirectory,
  chooseSaveFile,
  clearSaveLocation,
  deleteStoredHandle,
  describeSaveLocation,
  describeSaveProblem,
  ensureWritePermission,
  isAbortError,
  loadSaveLocation,
  probeDirectory,
  saveDeckJson,
  supportsDirectoryPicker,
  supportsFileSystemAccess,
  writeStoredHandle,
  FILE_HANDLE_KEY,
  DIRECTORY_HANDLE_KEY
} from '../utils/saveLocation';

/**
 * Remembers where the open deck is saved.
 *
 * The handles themselves live in a ref rather than in state: they are opaque
 * browser objects, they are read during a save (not during a render), and
 * putting them in state would re-render the editor every time a file is
 * written. State holds only the description the Save menu prints.
 *
 * @returns {{
 *   supported: boolean,
 *   canPickDirectory: boolean,
 *   locationLabel: string,
 *   hasFile: boolean,
 *   hasDirectory: boolean,
 *   saveJson: (options: { text: string, fileName: string, newFile?: boolean }) => Promise<object>,
 *   setDirectory: () => Promise<FileSystemDirectoryHandle | null>,
 *   setFile: (suggestedName?: string) => Promise<FileSystemFileHandle | null>,
 *   forget: () => Promise<void>
 * }}
 */
export default function useSaveLocation() {
  const handlesRef = useRef({ fileHandle: null, directoryHandle: null });
  const [locationLabel, setLocationLabel] = useState(() => describeSaveLocation({}));
  const [hasFile, setHasFile] = useState(false);
  const [hasDirectory, setHasDirectory] = useState(false);
  const [folderName, setFolderName] = useState('');

  const publish = useCallback(() => {
    const { fileHandle, directoryHandle } = handlesRef.current;
    setLocationLabel(describeSaveLocation({ fileHandle, directoryHandle }));
    setHasFile(Boolean(fileHandle));
    setHasDirectory(Boolean(directoryHandle));
    setFolderName(directoryHandle?.name || '');
  }, []);

  // Pull last session's handles back out of IndexedDB. Permission is not
  // requested here — that needs a click behind it, so it waits for the save.
  useEffect(() => {
    let cancelled = false;
    if (!supportsFileSystemAccess()) return undefined;

    loadSaveLocation()
      .then((stored) => {
        if (cancelled) return;
        handlesRef.current = stored;
        publish();
      })
      .catch((error) => {
        console.warn('[useSaveLocation] Could not restore the save location', error);
      });

    return () => { cancelled = true; };
  }, [publish]);

  const rememberFile = useCallback(async (handle) => {
    handlesRef.current = { ...handlesRef.current, fileHandle: handle || null };
    publish();
    if (handle) await writeStoredHandle(FILE_HANDLE_KEY, handle).catch(() => null);
  }, [publish]);

  const rememberDirectory = useCallback(async (handle) => {
    handlesRef.current = { ...handlesRef.current, directoryHandle: handle || null };
    publish();
    if (handle) await writeStoredHandle(DIRECTORY_HANDLE_KEY, handle).catch(() => null);
  }, [publish]);

  /**
   * Write the deck out. `newFile: true` is "Save +" — it never overwrites the
   * remembered file, but it does reuse the remembered folder.
   */
  const saveJson = useCallback(async ({ text, fileName, newFile = false }) => {
    const { fileHandle, directoryHandle } = handlesRef.current;
    const outcome = await saveDeckJson({ text, fileName, newFile, fileHandle, directoryHandle });

    if (outcome.cancelled) return outcome;
    if (outcome.fileHandle && outcome.fileHandle !== fileHandle) {
      await rememberFile(outcome.fileHandle);
    }
    if (outcome.directoryHandle && outcome.directoryHandle !== directoryHandle) {
      await rememberDirectory(outcome.directoryHandle);
    }
    return outcome;
  }, [rememberFile, rememberDirectory]);

  /**
   * "Set Dir": choose the folder this deck belongs in, before saving.
   *
   * The folder is tested before it is accepted — a few bytes written, read
   * back and deleted. A Google Drive folder will let itself be picked and then
   * swallow every write, and finding that out at Set Dir time costs nothing,
   * where finding out at save time costs the save.
   *
   * @returns {Promise<{ handle: FileSystemDirectoryHandle|null, ok: boolean,
   *   cancelled: boolean, failure: object|null }>}
   */
  const setDirectory = useCallback(async () => {
    const handle = await chooseDirectory({
      startIn: handlesRef.current.directoryHandle || undefined
    });
    if (!handle) return { handle: null, ok: false, cancelled: true, failure: null };

    await ensureWritePermission(handle);

    const probe = await probeDirectory(handle);
    if (!probe.ok) {
      // Deliberately not remembered: a folder that fails its probe would
      // otherwise become the place every later save quietly vanished into.
      return {
        handle,
        ok: false,
        cancelled: false,
        failure: describeSaveProblem(probe.error, { folderName: handle.name })
      };
    }

    await rememberDirectory(handle);
    return { handle, ok: true, cancelled: false, failure: null };
  }, [rememberDirectory]);

  /** "Save as": choose the exact file Save should overwrite from now on. */
  const setFile = useCallback(async (suggestedName) => {
    const handle = await chooseSaveFile({
      suggestedName,
      startIn: handlesRef.current.directoryHandle || null
    });
    if (!handle) return null;
    await rememberFile(handle);
    return handle;
  }, [rememberFile]);

  const forget = useCallback(async () => {
    handlesRef.current = { fileHandle: null, directoryHandle: null };
    publish();
    await clearSaveLocation();
  }, [publish]);

  /**
   * Forget only the remembered file, keeping the folder.
   *
   * Used after a failed save when a new folder has been chosen: the old file
   * handle points into the folder that just refused the write, and a plain
   * Save would go straight back to it.
   */
  const forgetFile = useCallback(async () => {
    handlesRef.current = { ...handlesRef.current, fileHandle: null };
    publish();
    await deleteStoredHandle(FILE_HANDLE_KEY).catch(() => null);
  }, [publish]);

  return {
    supported: supportsFileSystemAccess(),
    canPickDirectory: supportsDirectoryPicker(),
    locationLabel,
    folderName,
    hasFile,
    hasDirectory,
    saveJson,
    setDirectory,
    setFile,
    forget,
    forgetFile
  };
}

export { SaveMethod, isAbortError };
